import type { QRCode, Shop, ShopOwner } from '@notify/shared';

// ─── Settings types ───────────────────────────────────────────────────────────

export interface SystemStatus {
  database: 'connected' | 'unreachable';
  redis: 'connected' | 'unreachable';
  totalShops: number;
  activeShops: number;
  totalNotificationsSentToday: number;
  totalOrdersToday: number;
  activeSubscriptions: number;
}

export interface PlatformSettings {
  defaultTrialDays: number;
  defaultPrepTimeMinutes: number;
  defaultNotificationMessage: string;
  planLimits: {
    trial: number;
    basic: number;
    pro: number;
    enterprise: number;
  };
}

export interface AdminProfile {
  id: string;
  name: string;
  email: string;
  created_at: string;
}

import { api } from './api';

// ─── Response shapes ──────────────────────────────────────────────────────────

export interface ShopSubscription {
  planName: string;
  status: string;
  expiresAt: string;
}

export interface ShopWithDetails {
  id: string;
  name: string;
  location: string;
  ownerName: string;
  ownerEmail: string;
  rushMode: string;
  isActive: boolean;
  createdAt: string;
  subscription: ShopSubscription | null;
}

export interface ShopDetail extends ShopWithDetails {
  orderCount: number;
  prepTimeMinutes: number;
}

export interface AdminShopOwner {
  id: string;
  name: string;
  email: string;
}

export interface AdminSubscription {
  plan_name: string;
  status: string;
  subscribed_at: string;
  expires_at: string;
}

export interface AdminQRCode {
  id: string;
  token: string;
  qrImageUrl: string;
  status: string;
}

export interface AdminShopDetailData {
  shop: Shop & { createdAt: string };
  owner: AdminShopOwner | null;
  subscription: AdminSubscription | null;
  todayOrderCount: number;
  todayNotificationCount: number;
  qrCode: AdminQRCode | null;
}

export interface ShopOrderRow {
  id: string;
  shopId: string;
  batchId: string | null;
  orderReference: string;
  status: string;
  prepTimeOverride: number | null;
  prepDeadline: string | null;
  createdAt: string;
  notifiedAt: string | null;
}

export interface CreateShopPayload {
  shopName: string;
  location: string;
  ownerName: string;
  ownerEmail: string;
  ownerPassword: string;
}

export interface CreateShopResult {
  shop: Shop;
  owner: ShopOwner;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  title: string;
  description: string;
  price_monthly: number;
  duration_days: number;
  daily_notification_limit: number;
  max_shops: number;
  max_qr_codes: number;
  features: string[];
  is_active: boolean;
  is_popular: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface CreatePlanPayload {
  name: string;
  title: string;
  description: string;
  price_monthly: number;
  duration_days: number;
  daily_notification_limit: number;
  max_shops: number;
  max_qr_codes: number;
  features: string[];
  is_active: boolean;
  is_popular: boolean;
  sort_order: number;
}

// ─── Service functions ────────────────────────────────────────────────────────

export const adminService = {
  getShops: () =>
    api.get<ShopWithDetails[]>('/api/v1/admin/shops'),

  getShopDetail: (shopId: string) =>
    api.get<AdminShopDetailData>(`/api/v1/admin/shops/${shopId}`),

  updateShop: (shopId: string, data: { name?: string; location?: string }) =>
    api.patch<Shop>(`/api/v1/admin/shops/${shopId}`, data),

  getShopOrders: (shopId: string, date?: string, status?: string) =>
    api.get<ShopOrderRow[]>(`/api/v1/admin/shops/${shopId}/orders`, {
      params: { ...(date ? { date } : {}), ...(status ? { status } : {}) },
    }),

  updateShopOwner: (shopId: string, data: { name?: string; email?: string }) =>
    api.patch<AdminShopOwner>(`/api/v1/admin/shops/${shopId}/owner`, data),

  createShop: (data: CreateShopPayload) =>
    api.post<CreateShopResult>('/api/v1/admin/shops', data),

  toggleShopActive: (shopId: string) =>
    api.patch<Shop>(`/api/v1/admin/shops/${shopId}/toggle`),

  getQRCodes: (status?: string) =>
    api.get<QRCode[]>('/api/v1/qr', status ? { params: { status } } : undefined),

  generateQRCodes: (count: number) =>
    api.post<QRCode[]>('/api/v1/qr/generate', { count }),

  linkQRCode: (token: string, shopId: string) =>
    api.post<QRCode>('/api/v1/qr/link', { token, shopId }),

  retireQRCode: (codeId: string) =>
    api.patch<QRCode>(`/api/v1/qr/${codeId}/retire`),

  getSystemStatus: () =>
    api.get<SystemStatus>('/api/v1/admin/settings/status'),

  getSubscriptionPlans: () =>
    api.get<SubscriptionPlan[]>('/api/v1/admin/plans'),

  createSubscriptionPlan: (data: CreatePlanPayload) =>
    api.post<SubscriptionPlan>('/api/v1/admin/plans', data),

  updateSubscriptionPlan: (planId: string, data: Partial<CreatePlanPayload>) =>
    api.patch<SubscriptionPlan>(`/api/v1/admin/plans/${planId}`, data),

  deleteSubscriptionPlan: async (planId: string) => {
    await api.delete<{ message: string }>(`/api/v1/admin/plans/${planId}`);
  },

  togglePlanActive: (planId: string) =>
    api.patch<SubscriptionPlan>(`/api/v1/admin/plans/${planId}/toggle`),

  getPlatformSettings: () =>
    api.get<PlatformSettings>('/api/v1/admin/settings/platform'),

  getAdminProfile: () =>
    api.get<AdminProfile>('/api/v1/admin/settings/profile'),

  updateAdminProfile: (data: { name?: string; email?: string }) =>
    api.patch<AdminProfile>('/api/v1/admin/settings/profile', data),

  updateAdminPassword: (data: { currentPassword: string; newPassword: string }) =>
    api.patch<{ message: string }>('/api/v1/admin/settings/password', data),
};
